import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge } from 'react-bootstrap';
import EMMSectionHeader from './EMMSectionHeader';
import { useAccount } from '../contexts/AccountContext';

const Analytics = () => {
  const { selectedAccount, isAllAccountsSelected } = useAccount();
  const [analytics, setAnalytics] = useState({
    totalEmails: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
    unsubscribed: 0
  });

  const [campaignStats, setCampaignStats] = useState([]);

  useEffect(() => {
    // TODO: Fetch analytics from API - ACCOUNT SPECIFIC
    // No mock data - show empty state until real API is connected
    setAnalytics({
      totalEmails: 0,
      delivered: 0,
      opened: 0,
      clicked: 0,
      bounced: 0,
      unsubscribed: 0
    });
    setCampaignStats([]);
  }, [selectedAccount.id, isAllAccountsSelected]);

  const deliveryRate = analytics.totalEmails > 0 ? (analytics.delivered / analytics.totalEmails * 100).toFixed(1) : 0;
  const openRate = analytics.delivered > 0 ? (analytics.opened / analytics.delivered * 100).toFixed(1) : 0;
  const clickRate = analytics.opened > 0 ? (analytics.clicked / analytics.opened * 100).toFixed(1) : 0;

  return (
    <>
      <EMMSectionHeader title="Email Analytics" showHelp />
      <Container className="mt-3">
      <Row>
        <Col>
          <p className="text-muted mb-0">
            {isAllAccountsSelected 
              ? 'Track your email marketing performance across all accounts'
              : `Track email marketing performance for ${selectedAccount?.alias || 'selected account'}`
            }
          </p>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-primary">{analytics.totalEmails.toLocaleString()}</h4>
              <p className="text-muted mb-0">Total Sent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{analytics.delivered.toLocaleString()}</h4>
              <p className="text-muted mb-0">Delivered</p>
              <small className="text-success">{deliveryRate}%</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-info">{analytics.opened.toLocaleString()}</h4>
              <p className="text-muted mb-0">Opened</p>
              <small className="text-info">{openRate}%</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-warning">{analytics.clicked.toLocaleString()}</h4>
              <p className="text-muted mb-0">Clicked</p>
              <small className="text-warning">{clickRate}%</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-danger">{analytics.bounced.toLocaleString()}</h4>
              <p className="text-muted mb-0">Bounced</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-secondary">{analytics.unsubscribed.toLocaleString()}</h4>
              <p className="text-muted mb-0">Unsubscribed</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>Campaign Performance</h5>
            </Card.Header>
            <Card.Body>
              <Table responsive>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Sent</th>
                    <th>Delivered</th>
                    <th>Opened</th>
                    <th>Clicked</th>
                    <th>Open Rate</th>
                    <th>Click Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {campaignStats.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        <p>No campaign data available</p>
                        <small>Analytics will appear here once campaigns are sent.</small>
                      </td>
                    </tr>
                  ) : (
                    campaignStats.map((campaign, index) => (
                      <tr key={index}>
                        <td><strong>{campaign.name}</strong></td>
                        <td>{campaign.sent.toLocaleString()}</td>
                        <td>{campaign.delivered.toLocaleString()}</td>
                        <td>{campaign.opened.toLocaleString()}</td>
                        <td>{campaign.clicked.toLocaleString()}</td>
                        <td>
                          <span className="badge bg-info">{campaign.openRate}%</span>
                        </td>
                        <td>
                          <span className="badge bg-warning">{campaign.clickRate}%</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>Email Volume (Last 30 Days)</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center p-4">
                <p className="text-muted">Chart would be displayed here</p>
                <small>Integration with charting library needed</small>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>Top Performing Campaigns</h5>
            </Card.Header>
            <Card.Body>
              <div className="list-group list-group-flush">
                {campaignStats.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <p>No campaigns available</p>
                    <small>Top performing campaigns will appear here once campaigns are sent.</small>
                  </div>
                ) : (
                  campaignStats
                    .sort((a, b) => b.openRate - a.openRate)
                    .slice(0, 3)
                    .map((campaign, index) => (
                      <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{campaign.name}</strong>
                          <br />
                          <small className="text-muted">{campaign.sent} emails sent</small>
                        </div>
                        <span className="badge bg-success">{campaign.openRate}%</span>
                      </div>
                    ))
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </>
  );
};

export default Analytics;

